import React from 'react';
import { Play, ExternalLink, MapPin, Music, Radio, Disc, Calendar } from 'lucide-react';
import { PerformanceRecording } from '../types';

interface MediaSectionProps {
  recordings: PerformanceRecording[];
  onSelectRecording: (recording: PerformanceRecording) => void;
}

export const MediaSection: React.FC<MediaSectionProps> = ({
  recordings,
  onSelectRecording,
}) => {
  return (
    <section id="recordings" className="media-section">
      <div className="container">
        <div className="section-header">
          <span className="section-tag">Clarinet & Saxophone Performance</span>
          <h2 className="section-title">Recordings & Performances</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginTop: '0.5rem', maxWidth: '680px' }}>
            Live classical chamber performances and studio recording credits on clarinet and tenor saxophone.
          </p>
        </div>

        <div className="video-grid">
          {recordings.map((rec) => (
            <div key={rec.id} className="video-card">
              <div
                className="video-thumbnail-wrapper"
                onClick={() => onSelectRecording(rec)}
                role="button"
                tabIndex={0}
                aria-label={`Play ${rec.title}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onSelectRecording(rec);
                  }
                }}
              >
                <img
                  src={`https://i.ytimg.com/vi/${rec.youtube_id}/hqdefault.jpg`}
                  alt={rec.title}
                  className="video-thumbnail-img"
                  loading="lazy"
                />
                <div className="video-play-overlay">
                  <div className="video-play-btn">
                    <Play size={20} fill="#000000" />
                  </div>
                </div>

                {rec.category && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '0.75rem',
                      left: '0.75rem',
                      padding: '0.25rem 0.65rem',
                      borderRadius: 'var(--radius-full)',
                      background: 'rgba(0, 0, 0, 0.75)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      color: rec.category.includes('Studio') ? 'var(--accent-emerald)' : 'var(--accent-gold)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      zIndex: 2,
                    }}
                  >
                    {rec.category.includes('Studio') ? <Disc size={11} /> : <Music size={11} />}
                    <span>{rec.category}</span>
                  </div>
                )}
              </div>

              <div className="video-info">
                <div>
                  <span className="video-composer">{rec.composer}</span>
                  <h3 className="video-title">{rec.piece}</h3>
                  {rec.movement && (
                    <p className="video-movement">{rec.movement}</p>
                  )}
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <MapPin size={12} color="var(--accent-gold)" />
                      <span>{rec.venue}</span>
                    </span>
                    {rec.date && (
                      <>
                        <span style={{ opacity: 0.4 }}>&bull;</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Calendar size={12} color="var(--accent-gold)" />
                          <span>{rec.date}</span>
                        </span>
                      </>
                    )}
                  </p>
                </div>

                {/* Streaming Platform Badges */}
                {rec.streaming_links && Object.keys(rec.streaming_links).length > 0 && (
                  <div style={{ marginBottom: '0.85rem' }}>
                    <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', display: 'block', marginBottom: '0.4rem' }}>
                      Streaming & Media
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {Object.entries(rec.streaming_links).map(([platform, url]) => (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            padding: '0.25rem 0.6rem',
                            borderRadius: 'var(--radius-full)',
                            background: 'var(--bg-tertiary)',
                            border: '1px solid var(--border-subtle)',
                            color: 'var(--text-secondary)',
                            fontSize: '0.75rem',
                            textDecoration: 'none',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--accent-gold)';
                            e.currentTarget.style.color = 'var(--accent-gold)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'var(--border-subtle)';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                          }}
                        >
                          <Radio size={11} />
                          <span>{platform}</span>
                          <ExternalLink size={10} style={{ opacity: 0.6 }} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="performer-list">
                  <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
                    Performers & Personnel
                  </span>
                  {rec.performers.map((p, idx) => (
                    <div key={idx} className="performer-item">
                      <div>
                        <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{p.name}</strong>
                        <span style={{ color: 'var(--text-muted)', marginLeft: '0.35rem' }}>({p.instrument})</span>
                        {p.affiliation && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{p.affiliation}</div>
                        )}
                      </div>
                      {p.url && (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="performer-link"
                          title={`${p.name}'s portfolio`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
